/**
 * メール本文をプレーンテキスト / HTML に変換、mailto: リンク / .eml ファイルを生成。
 * 仕様書: docs/01_spec/spec_mail_system.md B-9
 */
import { inlineMarkdownToHtml, renderMailBodyText, type MailSection } from '@goenhub/shared';

interface RenderOptions {
  subject: string;
  sections: MailSection[];
  /** {{CTA_URL}} を実際の URL に置換 */
  ctaUrlOverride?: string;
}

/** プレーンテキスト形式に変換 */
export function toPlainText(opts: RenderOptions): string {
  return renderMailBodyText(opts.sections, { ctaUrlOverride: opts.ctaUrlOverride });
}

/** HTML 形式に変換（インラインスタイル付き） */
export function toHtml(opts: RenderOptions): string {
  const { sections, ctaUrlOverride } = opts;
  const blocks = sections
    .map((s) => {
      const content = s.content;
      // CTA セクション: ボタン化
      if (s.type === 'cta') {
        const url = ctaUrlOverride || s.cta_url_placeholder || '#';
        const beforeCta = content
          .replace(/\{\{CTA_URL\}\}/g, '')
          .trim();
        const safeUrl = escapeHtml(url);
        const buttonLabel = s.cta_button_label?.trim() || 'こちらへ';
        return `${paragraphify(beforeCta)}
<div style="text-align:center;margin:24px 0;">
  <a href="${safeUrl}" style="display:inline-block;padding:14px 32px;background:#4a7cff;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;font-size:14px;">${escapeHtml(buttonLabel)}</a>
</div>`;
      }
      // closing/ps の前に区切り線
      if (s.type === 'closing') {
        return `<hr style="border:none;border-top:1px solid #d0d7de;margin:24px 0;" />\n${paragraphify(content)}`;
      }
      return paragraphify(content);
    })
    .join('\n');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${escapeHtml(opts.subject)}</title>
</head>
<body style="font-family: 'Hiragino Sans', 'Yu Gothic', sans-serif; line-height: 1.7; color: #1a1a1a; max-width: 600px; margin: 0 auto; padding: 24px;">
${blocks}
</body>
</html>`;
}

function paragraphify(text: string): string {
  return text
    .split(/\n\n+/)
    .map(
      (p) =>
        `<p style="margin:0 0 16px 0;">${inlineMarkdownToHtml(escapeHtml(p)).replace(/\n/g, '<br>')}</p>`,
    )
    .join('\n');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** mailto: URL を構築（テキストモード用） */
export function buildMailto(opts: RenderOptions): string {
  const subject = encodeURIComponent(opts.subject);
  const body = encodeURIComponent(toPlainText(opts));
  return `mailto:?subject=${subject}&body=${body}`;
}

/** .eml ファイル（HTML メール）の Blob を生成 */
export function buildEmlBlob(opts: RenderOptions): Blob {
  // RFC 822 形式でエンコード
  const subjectEncoded = `=?UTF-8?B?${btoa(unescape(encodeURIComponent(opts.subject)))}?=`;
  const html = toHtml(opts);
  const htmlEncoded = btoa(unescape(encodeURIComponent(html)));
  const headers = [
    `Subject: ${subjectEncoded}`,
    'MIME-Version: 1.0',
    'Content-Type: text/html; charset=UTF-8',
    'Content-Transfer-Encoding: base64',
    '',
  ].join('\r\n');
  const eml = `${headers}\r\n${htmlEncoded}`;
  return new Blob([eml], { type: 'message/rfc822' });
}

/** .eml ファイルをダウンロード */
export function downloadEml(opts: RenderOptions, filename = 'mail.eml'): void {
  const blob = buildEmlBlob(opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
